import { useState, useEffect, useRef } from 'react';
import { BG_SUBTLE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_BAD, STATUS_WARN, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_BUTTON, FONT_LIST_ITEM, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_STAT_VALUE } from '../../theme';
import { dateStatus } from '../../utils/dateStatus';
import { appendChangeLog, isRecordImmutable } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import {
  listOrgCollection,
  writeOrgFile,
  writeBidirectional,
  deleteOrgFile,
  readOrgFile,
} from '../../storage/FileSystemAdapter';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import EvidenceSelector, { evidenceDisplayLabel } from '../shared/EvidenceSelector';
import TagEditor from '../shared/TagEditor';
import EntitySelector from '../shared/EntitySelector';
import SOA_CONTROLS from '../../data/soa-controls';
import { ISO_CLAUSES } from '../../iso-clauses';
import ConfirmDialog from '../shared/ConfirmDialog';
import ImportWizard from '../shared/ImportWizard';
import { DOCUMENT_STATUSES } from '../../utils/enums';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

const aId = (id) => id ? `A.${id}` : id;
const SOA_TITLE_MAP = Object.fromEntries(SOA_CONTROLS.map(c => [c.id, c]));

// ── STATUS COLORS ─────────────────────────────────────────────────────────────
const DOC_STATUS_COLORS = {
  'Draft':      { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'In Review':  { bg: '#e0e7ef', text: TEXT_DEFAULT },
  'Approved':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  'Superseded': { bg: BG_MUTED, text: TEXT_MUTED },
  'Retired':    { bg: BG_MUTED, text: TEXT_DISABLED },
};

// ── CLASSIFICATION CHIP COLORS ────────────────────────────────────────────────
function classColor(cls) {
  const u = (cls || '').toUpperCase();
  if (u === 'PUBLIC')       return { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK };
  if (u === 'INTERNAL')     return { bg: '#e0e7ef', text: TEXT_DEFAULT };
  if (u === 'CONFIDENTIAL') return { bg: STATUS_WARN.bg, text: STATUS_WARN.text };
  if (u === 'RESTRICTED')   return { bg: ERROR_BG, text: ERROR_TEXT };
  return { bg: BG_MUTED, text: TEXT_MUTED };
}

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:               'Información documentada',
    titlePolicy:         'Política de seguridad de la información',
    newDoc:              'Nuevo documento',
    importBtn:           'Importar CSV',
    importTitle:         'Importar documentos',
    ispZoneTitle:        'Política de seguridad de la información',
    ispEmptyHeading:     'No se ha designado una política de seguridad de la información',
    ispEmptyText:        'La política de seguridad de la información (ISO 27001 cláusula 5.2) define la dirección y el compromiso de la alta dirección con el SGSI. Cada organización debe tener exactamente una.',
    ispCreateBtn:        'Crear la política de seguridad de la información',
    ispBadgeText:        'Política de seguridad de la información · Cláusula 5.2',
    setAsIspBtn:         'Establecer como política de seguridad de la información',
    setAsIspConfirm:     'Esta acción establecerá este documento como la política de seguridad de la información. La política anterior (si existe) pasará a ser una política regular. Si la nueva política aún no está aprobada, esto puede afectar al cumplimiento de la cláusula 5.',
    setAsIspDirtyWarning:'Tienes cambios sin guardar en el formulario. Si continúas, se descartarán.',
    setAsIspConfirmBtn:  'Establecer como política',
    approvedOn:          'Aprobada el',
    save:                'Guardar',
    saving:              'Guardando…',
    saveOk:              '✓ Guardado',
    saveError:           'Error al guardar',
    unsavedLabel:        'Cambios sin guardar',
    unsavedConfirm:      'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:  'Salir sin guardar',
    saveAndLeave:        'Guardar y salir',
    loading:             'Cargando…',
    deleteConfirm:       '¿Eliminar este documento?',
    deleteSelected:        'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar {n} documento(s) seleccionado(s)? Esta acción no se puede deshacer.',
    nSelected:             '{n} seleccionado(s)',
    selectAll:             'Seleccionar todo',
    cntTotal:            'Total',
    cntApproved:         'Aprobados',
    cntDraft:            'Borradores',
    cntOther:            'Otros',
    filterType:          'Tipo',
    filterStatus:        'Estado',
    filterReviewStatus:  'Estado de revisión',
    filterTitle:         'Título',
    searchPlaceholder:   'Buscar por título…',
    all:                 'Todos',
    colRef:              'Referencia',
    colTitle:            'Título',
    colType:             'Tipo',
    colVersion:          'Versión',
    colStatus:           'Estado',
    colNextReview:       'Próxima revisión',
    colActions:          'Acciones',
    viewBtn:             'Ver',
    editBtn:             'Editar',
    deleteBtn:           'Eliminar',
    deleteFormBtn:       'Eliminar documento',
    emptyTitle:          'No hay documentos registrados.',
    emptyHint:           'Crea el primer documento con el botón "Nuevo documento".',
    formTitleNew:        'Nuevo documento',
    formTitleEdit:       'Editar documento',
    formTitleView:       'Ver documento',
    backToList:          '← Volver al listado',
    sectionA:            'Detalles del documento',
    sectionB:            'Referencias',
    sectionC1:           'Historial de cambios',
    sectionC1Tip:        'Registra cambios de contenido que resultan en una nueva versión del documento.',
    sectionC2:           'Historial de revisiones',
    sectionC2Tip:        'Registra revisiones periódicas formales, aunque el contenido no haya cambiado.',
    fTitle:              'Título',
    fType:               'Tipo',
    fVersion:            'Versión',
    fStatus:             'Estado',
    fOwner:              'Propietario',
    fApprovedBy:         'Aprobado por',
    fApprovalDate:       'Fecha de aprobación',
    fNextReview:         'Próxima revisión',
    fClassification:     'Clasificación',
    fDocumentId:         'ID del documento (opcional)',
    fDocumentIdHelper:   'Identificador propio de tu organización, si aplica.',
    fClauseRefs:         'Referencias de cláusulas',
    fControlIds:              'Controles del Anexo A',
    fControlIdsPlaceholder:   'Buscar control del Anexo A…',
    fAcknowledgmentRequired:  'Requiere acuse de recibo',
    tagPlaceholder:           'Añadir y pulsar Enter…',
    fLanguage:                'Idioma del documento',
    fEffectiveDate:           'Fecha de vigencia',
    fApprovalEvidenceId:      'Archivo del documento',
    languageSelectPlaceholder: 'Selecciona…',
    languageES:                'Español',
    languageEN:                'Inglés',
    chColVersion:        'Versión',
    chColDate:           'Fecha',
    chColAuthor:         'Autor',
    chColSummary:        'Resumen',
    chAddEntry:          'Añadir',
    rvColDate:           'Fecha',
    rvColReviewedBy:     'Revisado por',
    rvColOutcome:        'Resultado',
    rvColNotes:          'Notas',
    rvColEvidence:       'Evidencia',
    rvOutcomeNoChanges:  'Sin cambios',
    rvOutcomeUpdated:    'Actualizado',
    rvOutcomeSuperseded: 'Reemplazado',
    rvOutcomeRetired:    'Retirado',
    rvAddEntry:          'Añadir',
    rvHintChanged:       'Si el contenido cambió, recuerda añadir también una entrada en el historial de cambios.',
    rvNextReviewUpdated: 'Fecha de próxima revisión actualizada automáticamente.',
    formSave:            'Guardar documento',
    formCancel:          'Cancelar',
    formValidationError: 'Por favor completa los campos obligatorios marcados con *.',
    errorRequired:       'Campo obligatorio',
    errorRequiredWhenApproved: 'Obligatorio cuando el estado es Aprobado',
    chDefaultSummary:    'Creación',
    changeHistoryRequired: 'Añade al menos una entrada de Historial de cambios antes de guardar.',
    required:            '*',
    requiredLegend:      '* Campos obligatorios',
    frozenBanner:        '🔒 Este registro es de solo lectura. Para modificarlo, reabre desde Ajustes → Reabrir registros cerrados.',
    badgeOverdue:        'Vencido',
    badgeSoon:           'Vence pronto',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
    typeLabels: {
      'Policy':    'Política',
      'Procedure': 'Procedimiento',
      'Standard':  'Norma',
      'Guideline': 'Directriz',
      'Record':    'Registro',
      'Template':  'Plantilla',
      'Other':     'Otro',
    },
    statusLabels: {
      'Draft':      'Borrador',
      'In Review':  'En revisión',
      'Approved':   'Aprobado',
      'Superseded': 'Reemplazado',
      'Retired':    'Retirado',
    },
    reviewOutcomeLabels: {
      'No changes': 'Sin cambios',
      'Updated':    'Actualizado',
      'Superseded': 'Reemplazado',
      'Retired':    'Retirado',
    },
  },
  en: {
    title:               'Documented information',
    titlePolicy:         'Information security policy',
    newDoc:              'New document',
    importBtn:           'Import CSV',
    importTitle:         'Import documents',
    ispZoneTitle:        'Information Security Policy',
    ispEmptyHeading:     'No Information Security Policy has been designated yet',
    ispEmptyText:        "The Information Security Policy (ISO 27001 clause 5.2) sets top management's direction and commitments for the ISMS. Each organization must have exactly one.",
    ispCreateBtn:        'Create the Information Security Policy',
    ispBadgeText:        'Information Security Policy · Clause 5.2',
    setAsIspBtn:         'Set as Information Security Policy',
    setAsIspConfirm:     'This action will mark the current document as the Information Security Policy. The previous policy (if any) will become a regular policy. If the new policy is not yet approved, this may affect Clause 5 compliance.',
    setAsIspDirtyWarning:'You have unsaved changes in the form. If you continue, they will be discarded.',
    setAsIspConfirmBtn:  'Set as policy',
    approvedOn:          'Approved on',
    save:                'Save',
    saving:              'Saving…',
    saveOk:              '✓ Saved',
    saveError:           'Save error',
    unsavedLabel:        'Unsaved changes',
    unsavedConfirm:      'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:  'Leave without saving',
    saveAndLeave:        'Save and leave',
    loading:             'Loading…',
    deleteConfirm:       'Delete this document?',
    deleteSelected:        'Delete selected',
    deleteSelectedConfirm: 'Delete {n} selected document(s)? This cannot be undone.',
    nSelected:             '{n} selected',
    selectAll:             'Select all',
    cntTotal:            'Total',
    cntApproved:         'Approved',
    cntDraft:            'Draft',
    cntOther:            'Other',
    filterType:          'Type',
    filterStatus:        'Status',
    filterReviewStatus:  'Review status',
    filterTitle:         'Title',
    searchPlaceholder:   'Search by title…',
    all:                 'All',
    colRef:              'Reference',
    colTitle:            'Title',
    colType:             'Type',
    colVersion:          'Version',
    colStatus:           'Status',
    colNextReview:       'Next review',
    colActions:          'Actions',
    viewBtn:             'View',
    editBtn:             'Edit',
    deleteBtn:           'Delete',
    deleteFormBtn:       'Delete document',
    emptyTitle:          'No documents registered.',
    emptyHint:           'Create the first document using the "New document" button.',
    formTitleNew:        'New document',
    formTitleEdit:       'Edit document',
    formTitleView:       'View document',
    backToList:          '← Back to list',
    sectionA:            'Document details',
    sectionB:            'References',
    sectionC1:           'Change history',
    sectionC1Tip:        'Records content changes that result in a new document version.',
    sectionC2:           'Review history',
    sectionC2Tip:        'Records formal periodic reviews, whether or not the content changed.',
    fTitle:              'Title',
    fType:               'Type',
    fVersion:            'Version',
    fStatus:             'Status',
    fOwner:              'Owner',
    fApprovedBy:         'Approved by',
    fApprovalDate:       'Approval date',
    fNextReview:         'Next review date',
    fClassification:     'Classification',
    fDocumentId:         'Document ID (optional)',
    fDocumentIdHelper:   "Your organization's own document identifier, if applicable.",
    fClauseRefs:         'Clause references',
    fControlIds:              'Annex A controls',
    fControlIdsPlaceholder:   'Search Annex A control…',
    fAcknowledgmentRequired:  'Acknowledgment required',
    tagPlaceholder:           'Add and press Enter…',
    fLanguage:                'Document language',
    fEffectiveDate:           'Effective date',
    fApprovalEvidenceId:      'Document file',
    languageSelectPlaceholder: 'Select…',
    languageES:                'Spanish',
    languageEN:                'English',
    chColVersion:        'Version',
    chColDate:           'Date',
    chColAuthor:         'Author',
    chColSummary:        'Summary',
    chAddEntry:          'Add',
    rvColDate:           'Date',
    rvColReviewedBy:     'Reviewed by',
    rvColOutcome:        'Outcome',
    rvColNotes:          'Notes',
    rvColEvidence:       'Evidence',
    rvOutcomeNoChanges:  'No changes',
    rvOutcomeUpdated:    'Updated',
    rvOutcomeSuperseded: 'Superseded',
    rvOutcomeRetired:    'Retired',
    rvAddEntry:          'Add',
    rvHintChanged:       'If the content changed, remember to also add an entry in the change history.',
    rvNextReviewUpdated: 'Next review date updated automatically.',
    formSave:            'Save document',
    formCancel:          'Cancel',
    formValidationError: 'Please complete the required fields marked with *.',
    errorRequired:       'Required field',
    errorRequiredWhenApproved: 'Required when status is Approved',
    chDefaultSummary:    'Creation',
    changeHistoryRequired: 'Add at least one Change History entry before saving.',
    required:            '*',
    requiredLegend:      '* Required fields',
    frozenBanner:        '🔒 This record is read-only. To modify it, reopen from Settings → Reopen closed records.',
    badgeOverdue:        'Overdue',
    badgeSoon:           'Due soon',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
    typeLabels: {
      'Policy':    'Policy',
      'Procedure': 'Procedure',
      'Standard':  'Standard',
      'Guideline': 'Guideline',
      'Record':    'Record',
      'Template':  'Template',
      'Other':     'Other',
    },
    statusLabels: {
      'Draft':      'Draft',
      'In Review':  'In Review',
      'Approved':   'Approved',
      'Superseded': 'Superseded',
      'Retired':    'Retired',
    },
    reviewOutcomeLabels: {
      'No changes': 'No changes',
      'Updated':    'Updated',
      'Superseded': 'Superseded',
      'Retired':    'Retired',
    },
  },
};

const DOC_TYPES      = ['Policy', 'Procedure', 'Standard', 'Guideline', 'Record', 'Template', 'Other'];
const DOC_STATUSES   = DOCUMENT_STATUSES;
const REVIEW_OUTCOMES = ['No changes', 'Updated', 'Superseded', 'Retired'];

// ── HELPERS ───────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function makeDateCompact() {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
}

function makeDocId(seq) {
  return `doc-${makeDateCompact()}-${String(seq).padStart(3, '0')}`;
}

function makeRef(seq, docPrefix) {
  const year = new Date().getFullYear();
  return `${docPrefix}-${year}-${String(seq).padStart(3, '0')}`;
}

function addMonths(dateStr, months) {
  const d = new Date(dateStr);
  // Guard: an unparseable input yields an Invalid Date whose toISOString throws.
  // Return '' rather than crashing the caller (e.g. non-ISO data in changeHistory).
  if (isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

// Auto-derive Document.version from changeHistory: the entry with the most recent
// date determines the version. Returns the fallback when no valid dated entries exist.
function deriveVersion(changeHistory, fallback) {
  if (!Array.isArray(changeHistory) || changeHistory.length === 0) return fallback;
  let best = null;
  for (const e of changeHistory) {
    if (!e || !e.date) continue;
    if (!best || e.date >= best.date) best = e;
  }
  return best && best.version ? best.version : fallback;
}

// Recalculate nextReviewDate as max(all CH dates, all RH dates) + period months.
// Only parseable dates are eligible; non-ISO / malformed entries are discarded so
// they can neither be selected as the max nor reach addMonths. The maximum is chosen
// by parsed time value (chronological), not by lexicographic string comparison —
// for valid ISO dates the result is identical to a string-max, but a malformed value
// like '30/01/2023' can no longer outrank a real ISO date.
function computeNextReviewDate(changeHistory, reviewHistory, periodMonths) {
  const dates = [];
  if (Array.isArray(changeHistory)) for (const e of changeHistory) { if (e && e.date && !isNaN(new Date(e.date).getTime())) dates.push(e.date); }
  if (Array.isArray(reviewHistory)) for (const e of reviewHistory) { if (e && e.date && !isNaN(new Date(e.date).getTime())) dates.push(e.date); }
  if (dates.length === 0) return '';
  const maxDate = dates.reduce((a, b) => (new Date(a).getTime() >= new Date(b).getTime() ? a : b));
  return addMonths(maxDate, periodMonths);
}

function blankFormValues(org, typeFilter, isIsp, lang) {
  const t = T[lang] || T.en;
  return {
    documentId:         '',
    title:              '',
    type:               typeFilter || 'Policy',
    version:            '1.0',
    status:             'Draft',
    owner:              '',
    approvedBy:         [],
    approvalDate:       '',
    nextReviewDate:     '',
    classification:     (org && org.defaultClassification) || 'INTERNAL',
    clauseRefs:         [],
    linkedControlIds:   [],
    acknowledgmentRequired: false,
    norms:              [],
    language:           null,
    effectiveDate:      null,
    approvalEvidenceId: null,
    isInformationSecurityPolicy: !!isIsp,
    changeHistory:      [],
    reviewHistory:      [],
    // ephemeral change-history entry inputs (pre-filled for the mandatory first entry)
    chVersion:      '1.0',
    chDate:         todayStr(),
    chAuthor:       '',
    chSummary:      t.chDefaultSummary,
    // ephemeral review-history entry inputs
    rvDate:         todayStr(),
    rvReviewedBy:   '',
    rvOutcome:      'No changes',
    rvNotes:        '',
    rvEvidenceId:   null,
  };
}

function docToFormValues(doc) {
  const derivedVersion = deriveVersion(doc.changeHistory, doc.version || '1.0');
  return {
    documentId:     doc.documentId || '',
    title:          doc.title || '',
    type:           doc.type  || 'Policy',
    version:        derivedVersion,
    status:         doc.status  || 'Draft',
    owner:          doc.owner   || '',
    // Normalise legacy string → array
    approvedBy: Array.isArray(doc.approvedBy)
      ? doc.approvedBy
      : doc.approvedBy ? [doc.approvedBy] : [],
    approvalDate:   doc.approvalDate   || '',
    nextReviewDate: doc.nextReviewDate || '',
    classification: doc.classification || '',
    clauseRefs:     doc.clauseRefs     || [],
    linkedControlIds: doc.linkedControlIds || doc.controlIds || [],
    linkedRiskIds:     Array.isArray(doc.linkedRiskIds) ? doc.linkedRiskIds : [],
    linkedIncidentIds: Array.isArray(doc.linkedIncidentIds) ? doc.linkedIncidentIds : [],
    linkedAssetIds:    Array.isArray(doc.linkedAssetIds) ? doc.linkedAssetIds : [],
    acknowledgmentRequired: typeof doc.acknowledgmentRequired === 'boolean' ? doc.acknowledgmentRequired : false,
    norms:             Array.isArray(doc.norms) ? doc.norms : [],
    language:           doc.language           || null,
    effectiveDate:      doc.effectiveDate      || null,
    approvalEvidenceId: doc.approvalEvidenceId || null,
    isInformationSecurityPolicy: doc.isInformationSecurityPolicy === true,
    changeHistory:  doc.changeHistory  || [],
    reviewHistory:  doc.reviewHistory  || [],
    chVersion:      derivedVersion,
    chDate:         todayStr(),
    chAuthor:       '',
    chSummary:      '',
    rvDate:         todayStr(),
    rvReviewedBy:   '',
    rvOutcome:      'No changes',
    rvNotes:        '',
    rvEvidenceId:   null,
  };
}

function stripEphemeral(values) {
  const {  // eslint-disable-line no-unused-vars
    chVersion, chDate, chAuthor, chSummary,
    rvDate, rvReviewedBy, rvOutcome, rvNotes, rvEvidenceId,
    ...clean
  } = values;
  return clean;
}

// ── SUMMARY CARD ──────────────────────────────────────────────────────────────
function SummaryCard({ value, label, color }) {
  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
      padding: '0.9rem 1.25rem',
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT, letterSpacing: '0.04em',
        textTransform: 'uppercase', marginBottom: '0.35rem',
      }}>
        {label}
      </div>
      <div style={{
        ...FONT_STAT_VALUE, color,
      }}>
        {value}
      </div>
    </div>
  );
}

// ── SECTION HEADER WITH TOOLTIP ───────────────────────────────────────────────
function SectionHeader({ label, tip, style: extraStyle }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.4rem',
      ...FONT_TINY,
      color: TEXT_MUTED, letterSpacing: '0.05em', textTransform: 'uppercase',
      fontWeight: 600,
      ...extraStyle,
    }}>
      {label}
      <span
        title={tip}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 15, height: 15,
          borderRadius: '50%',
          background: STATUS_NEUTRAL.bg,
          color: TEXT_MUTED,
          cursor: 'default',
          flexShrink: 0,
          ...FONT_TINY,
          fontWeight: 700,
          letterSpacing: 0,
          textTransform: 'none',
        }}
      >
        i
      </span>
    </div>
  );
}

// ── DOCUMENT FORM ─────────────────────────────────────────────────────────────
function DocumentForm({ t, org, lang, mode, docId, initialValues, saving, saveError, onSave, onCancel, onDelete, onFormChange, saveRequestTick, dirty, typeFilter, onSetAsIsp }) {
  const isView = mode === 'view';
  // §11 rule 34 — read-only banner shown only when the form is in view mode
  // because the record is in a terminal/immutable status (Superseded/Retired),
  // not when the user voluntarily opened a non-immutable record in view mode.
  const frozen = isView && isRecordImmutable({ status: initialValues.status }, 'document');
  const clauseItems = Object.entries(ISO_CLAUSES).map(([id, titles]) => ({
    id,
    label: `${id} — ${lang === 'es' ? titles.es : titles.en}`,
  }));
  const [values,         setValues]       = useState(initialValues);
  const [formErrors,     setFormErrors]   = useState({});
  const [reviewMsg,      setReviewMsg]    = useState(false);
  const [controlOptions, setControlOptions] = useState([]);
  const [people,         setPeople]       = useState([]);
  const [evidences,      setEvidences]    = useState([]);
  const hasChangedRef = useRef(false);
  const changeHistoryRef = useRef(null);

  useEffect(() => {
    readOrgFile(org.id, 'controls/soa.json')
      .then((data) => {
        if (data && Array.isArray(data.controls)) {
          setControlOptions(data.controls.map((c) => ({
            id: c.id,
            label: `${aId(c.id)} — ${lang === 'es' ? (SOA_TITLE_MAP[c.id]?.title_es || c.title) : c.title}`,
          })));
        }
      })
      .catch(() => {});
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    readOrgFile(org.id, 'people.json')
      .then((data) => {
        if (!cancelled) setPeople(Array.isArray(data.people) ? data.people : []);
      })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [org.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    listOrgCollection(org.id, 'evidence')
      .then((data) => { if (!cancelled) setEvidences(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setEvidences([]); });
    return () => { cancelled = true; };
  }, [org.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markChange = () => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const set = (field, val) => {
    markChange();
    setValues((prev) => ({ ...prev, [field]: val }));
  };

  // Auto-calculate nextReviewDate when approvalDate changes
  const handleApprovalDateChange = (dateStr) => {
    markChange();
    setValues((prev) => {
      const next = { ...prev, approvalDate: dateStr };
      if (dateStr) {
        const period = (org && org.documentReviewPeriodMonths) || 12;
        next.nextReviewDate = addMonths(dateStr, period);
      }
      return next;
    });
    clearErr('approvalDate');
  };

  // Classification options from org profile
  const classOptions = (org && org.documentClassification && org.documentClassification.length > 0)
    ? org.documentClassification
    : null;

  // Enhancement 3: required-field validation
  const handleSubmit = () => {
    const errs = {};
    if (!values.title.trim())   errs.title   = true;
    if (!values.version.trim()) errs.version = true;
    if (!values.owner.trim())   errs.owner   = true;
    if (!docId && (!Array.isArray(values.changeHistory) || values.changeHistory.length === 0)) {
      errs.changeHistory = true;
    }
    if (values.status === 'Approved') {
      if (!Array.isArray(values.approvedBy) || values.approvedBy.length === 0) {
        errs.approvedBy = true;
      }
      if (values.approvalDate == null || values.approvalDate === '') {
        errs.approvalDate = true;
      }
    }
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      // §9: scroll to first error. The changeHistory error is the only one not
      // tied to an input field with its own focus — scroll to the section if
      // it is the first error to surface.
      if (errs.changeHistory && !errs.title && !errs.version && !errs.owner) {
        changeHistoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    setFormErrors({});
    onSave(values);
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const clearErr = (field) => {
    if (formErrors[field]) setFormErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  // Change history: add entry (version*, date*, author* required)
  const handleAddChangeEntry = () => {
    if (!values.chVersion.trim() || !values.chDate || !values.chAuthor.trim()) return;
    markChange();
    const entry = {
      version: values.chVersion.trim(),
      date:    values.chDate,
      author:  values.chAuthor.trim(),
      summary: values.chSummary.trim(),
    };
    const period = (org && org.documentReviewPeriodMonths) || 12;
    setValues((prev) => {
      const updatedCh = [...prev.changeHistory, entry];
      const newVersion = deriveVersion(updatedCh, prev.version || '1.0');
      const newNextReview = computeNextReviewDate(updatedCh, prev.reviewHistory, period);
      return {
        ...prev,
        changeHistory:  updatedCh,
        version:        newVersion,
        nextReviewDate: newNextReview,
        chVersion: newVersion, chDate: todayStr(), chAuthor: '', chSummary: '',
      };
    });
    clearErr('changeHistory');
  };

  // Review history: add entry (date*, reviewedBy*, outcome* required)
  const handleAddReviewEntry = () => {
    if (!values.rvDate || !values.rvReviewedBy.trim() || !values.rvOutcome) return;
    markChange();
    const entry = {
      date:        values.rvDate,
      reviewedBy:  values.rvReviewedBy.trim(),
      outcome:     values.rvOutcome,
      notes:       values.rvNotes.trim(),
      evidenceId:  values.rvEvidenceId,
    };
    const period = (org && org.documentReviewPeriodMonths) || 12;
    setValues((prev) => {
      const updatedRh = [...prev.reviewHistory, entry];
      const newNextReview = computeNextReviewDate(prev.changeHistory, updatedRh, period);
      return {
        ...prev,
        reviewHistory:  updatedRh,
        nextReviewDate: newNextReview,
        rvDate: todayStr(), rvReviewedBy: '', rvOutcome: 'No changes',
        rvNotes: '', rvEvidenceId: null,
      };
    });
    setReviewMsg(true);
    setTimeout(() => setReviewMsg(false), 3000);
  };

  const isNew = !docId;

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputSt = (hasErr) => ({
    width: '100%',
    ...FONT_BODY,
    border: `1px solid ${hasErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
    borderRadius: 6,
    padding: '0.45rem 0.65rem',
    background: BG_SURFACE,
    color: TEXT_DEFAULT,
    outline: 'none',
    boxSizing: 'border-box',
  });

  const labelSt = {
    ...FONT_TINY,
    color: TEXT_DISABLED,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '0.35rem',
  };

  const fieldSt = { marginBottom: '1rem' };

  const sectionSt = {
    marginBottom: '1rem',
    marginTop: '1.5rem',
    borderTop: `1px solid ${BORDER_DEFAULT}`,
    paddingTop: '1.25rem',
  };

  const thSt = {
    ...FONT_TINY,
    color: TEXT_MUTED,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '0.4rem 0.6rem',
    textAlign: 'left',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
    background: BG_SUBTLE,
    whiteSpace: 'nowrap',
  };

  const tdSt = {
    padding: '0.45rem 0.6rem',
    borderBottom: `1px solid ${BG_SHELL}`,
    ...FONT_LABEL,
    color: TEXT_DEFAULT,
    verticalAlign: 'middle',
  };

  const chInputSt = {
    width: '100%',
    ...FONT_LABEL,
    border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 4,
    padding: '0.25rem 0.4rem',
    background: BG_SURFACE,
    color: TEXT_DEFAULT,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const hasFormErrors = Object.keys(formErrors).length > 0;

  const outcomeLabel = (v) => {
    if (v === 'No changes') return t.rvOutcomeNoChanges;
    if (v === 'Updated')    return t.rvOutcomeUpdated;
    if (v === 'Superseded') return t.rvOutcomeSuperseded;
    if (v === 'Retired')    return t.rvOutcomeRetired;
    return v;
  };

  return (
    <div style={{
      background: BG_SURFACE,
      border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 10,
      padding: '1.5rem',
      marginTop: '1.5rem',
      position: 'relative',
    }}>
      <button
        type="button"
        onClick={onCancel}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: BRAND_PRIMARY, ...FONT_LABEL,
          letterSpacing: '0.04em',
          display: 'block', marginBottom: '0.5rem',
        }}
      >
        {t.backToList}
      </button>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '1rem', flexWrap: 'wrap', margin: '0 0 1.5rem',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <h3 style={{
            ...FONT_SECTION_TITLE, fontWeight: 700, fontSize: '1rem',
            color: TEXT_HEADING, margin: 0,
          }}>
            {isView ? t.formTitleView : (isNew ? t.formTitleNew : t.formTitleEdit)}
          </h3>
          {values.isInformationSecurityPolicy && (
            <span style={{
              display: 'inline-flex', alignSelf: 'flex-start',
              background: STATUS_GOOD.bg, color: BRAND_PRIMARY_DARK,
              borderRadius: 5, padding: '0.2rem 0.55rem',
              ...FONT_SMALL,
              fontWeight: 600, letterSpacing: '0.03em',
            }}>
              {t.ispBadgeText}
            </span>
          )}
        </div>
        {onSetAsIsp && !isNew && !frozen && values.type === 'Policy' && !values.isInformationSecurityPolicy && (
          <button
            type="button"
            onClick={onSetAsIsp}
            style={{
              background: 'transparent', color: BRAND_PRIMARY_DARK,
              border: `1px solid ${BRAND_PRIMARY}`, borderRadius: 6,
              padding: '0.4rem 0.85rem', cursor: 'pointer',
              ...FONT_LABEL,
              letterSpacing: '0.03em', whiteSpace: 'nowrap',
            }}
          >
            {t.setAsIspBtn}
          </button>
        )}
      </div>

      {frozen && (
        <div style={{
          background: STATUS_NEUTRAL.bg, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
          ...FONT_LABEL, color: TEXT_DEFAULT,
        }}>
          {t.frozenBanner}
        </div>
      )}

      {!isView && (
        <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>
      )}

      <fieldset disabled={isView || saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── SECTION A ─────────────────────────────────────────────────── */}
      <div style={{
        ...FONT_TINY,
        color: TEXT_MUTED, letterSpacing: '0.05em', textTransform: 'uppercase',
        fontWeight: 600, marginBottom: '1rem',
      }}>
        {t.sectionA}
      </div>

      {/* Title (required) */}
      <div style={fieldSt}>
        <label style={labelSt}>{t.fTitle} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
        <input
          value={values.title}
          onChange={(e) => { set('title', e.target.value); clearErr('title'); }}
          style={inputSt(!!formErrors.title)}
        />
        {formErrors.title && (
          <span style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.2rem', display: 'block' }}>
            {t.errorRequired}
          </span>
        )}
      </div>

      {/* Document ID (optional) */}
      <div style={fieldSt}>
        <label style={labelSt}>{t.fDocumentId}</label>
        <input
          value={values.documentId}
          onChange={(e) => set('documentId', e.target.value)}
          placeholder="e.g. POL-001"
          style={inputSt(false)}
        />
        <span style={{
          ...FONT_TINY,
          color: TEXT_DISABLED, marginTop: '0.25rem', display: 'block',
        }}>
          {t.fDocumentIdHelper}
        </span>
      </div>

      {/* 2-column grid: type*, version*, status*, owner* */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {!typeFilter && (
          <div style={fieldSt}>
            <label style={labelSt}>{t.fType} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <select
              value={values.type}
              onChange={(e) => set('type', e.target.value)}
              style={{ ...inputSt(false), cursor: 'pointer' }}
            >
              {DOC_TYPES.map((tp) => <option key={tp} value={tp}>{t.typeLabels[tp]}</option>)}
            </select>
          </div>
        )}
        <div style={fieldSt}>
          <label style={labelSt}>{t.fVersion}</label>
          <div
            style={{
              ...inputSt(false),
              background: BG_SUBTLE,
              color: TEXT_MUTED,
              cursor: 'default',
              userSelect: 'text',
            }}
          >
            {values.version || '—'}
          </div>
        </div>
        <div style={fieldSt}>
          <label style={labelSt}>{t.fStatus} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <select
            value={values.status}
            onChange={(e) => {
              const nextStatus = e.target.value;
              set('status', nextStatus);
              if (nextStatus !== 'Approved') {
                clearErr('approvedBy');
                clearErr('approvalDate');
              }
            }}
            style={{ ...inputSt(false), cursor: 'pointer' }}
          >
            {DOC_STATUSES.map((s) => <option key={s} value={s}>{t.statusLabels[s]}</option>)}
          </select>
        </div>
        <div style={fieldSt}>
          <label style={labelSt}>{t.fOwner} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <PeopleSelector orgId={org.id} lang={lang} value={values.owner || ''} onChange={val => { set('owner', val); clearErr('owner'); }} disabled={saving} />
          {formErrors.owner && (
            <span style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.2rem', display: 'block' }}>
              {t.errorRequired}
            </span>
          )}
        </div>
      </div>

      {/* Approved by — full width tag editor */}
      <div style={fieldSt}>
        <label style={labelSt}>
          {t.fApprovedBy}
          {values.status === 'Approved' && <span style={{ color: ERROR_TEXT }}> {t.required}</span>}
        </label>
        <PeopleSelector orgId={org.id} lang={lang} value={Array.isArray(values.approvedBy) ? values.approvedBy : []} onChange={val => { set('approvedBy', val); clearErr('approvedBy'); }} multiple disabled={saving} />
        {formErrors.approvedBy && (
          <span style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.2rem', display: 'block' }}>
            {t.errorRequiredWhenApproved}
          </span>
        )}
      </div>

      {/* Classification + dates */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={fieldSt}>
          <label style={labelSt}>{t.fClassification}</label>
          {classOptions ? (
            <select
              value={values.classification}
              onChange={(e) => set('classification', e.target.value)}
              style={{ ...inputSt(false), cursor: 'pointer' }}
            >
              {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input
              value={values.classification}
              onChange={(e) => set('classification', e.target.value)}
              style={inputSt(false)}
            />
          )}
        </div>
        <div style={fieldSt}>
          <label style={labelSt}>
            {t.fApprovalDate}
            {values.status === 'Approved' && <span style={{ color: ERROR_TEXT }}> {t.required}</span>}
          </label>
          <input
            type="date"
            value={values.approvalDate}
            onChange={(e) => handleApprovalDateChange(e.target.value)}
            style={inputSt(!!formErrors.approvalDate)}
          />
          {formErrors.approvalDate && (
            <span style={{ ...FONT_SMALL, color: ERROR_TEXT, marginTop: '0.2rem', display: 'block' }}>
              {t.errorRequiredWhenApproved}
            </span>
          )}
        </div>
        <div style={fieldSt}>
          <label style={labelSt}>{t.fNextReview}</label>
          <input
            type="date"
            value={values.nextReviewDate}
            onChange={(e) => set('nextReviewDate', e.target.value)}
            style={inputSt(false)}
          />
        </div>
      </div>

      {/* ── SECTION B ─────────────────────────────────────────────────── */}
      <div style={sectionSt}>
        <div style={{ ...FONT_TINY, color: TEXT_MUTED, letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '1rem' }}>
          {t.sectionB}
        </div>
      </div>

      <div style={fieldSt}>
        <label style={labelSt}>{t.fClauseRefs}</label>
        <EntitySelector
          items={clauseItems}
          selectedIds={values.clauseRefs}
          onAdd={(id) => set('clauseRefs', [...values.clauseRefs, id])}
          onRemove={(id) => set('clauseRefs', values.clauseRefs.filter((x) => x !== id))}
          placeholder={lang === 'es' ? 'Buscar cláusula ISO 27001…' : 'Search ISO 27001 clause…'}
          disabled={isView}
        />
      </div>

      <div style={fieldSt}>
        <label style={labelSt}>{t.fControlIds}</label>
        {controlOptions.length > 0 ? (
          <EntitySelector
            items={controlOptions}
            selectedIds={values.linkedControlIds}
            onAdd={(id) => set('linkedControlIds', [...values.linkedControlIds, id])}
            onRemove={(id) => set('linkedControlIds', values.linkedControlIds.filter((x) => x !== id))}
            placeholder={t.fControlIdsPlaceholder}
          />
        ) : (
          <TagEditor
            tags={values.linkedControlIds}
            onChange={(tags) => set('linkedControlIds', tags)}
            placeholder={t.tagPlaceholder}
          />
        )}
      </div>

      <div style={fieldSt}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: isView ? 'default' : 'pointer', ...FONT_LABEL, color: TEXT_DEFAULT }}>
          <input
            type="checkbox"
            checked={!!values.acknowledgmentRequired}
            onChange={(e) => set('acknowledgmentRequired', e.target.checked)}
            disabled={isView}
          />
          {t.fAcknowledgmentRequired}
        </label>
      </div>

      {/* ── New schema fields: language, effectiveDate, approvalEvidenceId ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={fieldSt}>
          <label style={labelSt}>{t.fLanguage}</label>
          <select
            value={values.language || ''}
            onChange={(e) => set('language', e.target.value || null)}
            style={{ ...inputSt(false), cursor: 'pointer' }}
          >
            <option value="">{t.languageSelectPlaceholder}</option>
            <option value="es">{t.languageES}</option>
            <option value="en">{t.languageEN}</option>
          </select>
        </div>
        <div style={fieldSt}>
          <label style={labelSt}>{t.fEffectiveDate}</label>
          <input
            type="date"
            value={values.effectiveDate || ''}
            onChange={(e) => set('effectiveDate', e.target.value || null)}
            style={inputSt(false)}
          />
        </div>
        <div style={fieldSt}>
          <label style={labelSt}>{t.fApprovalEvidenceId}</label>
          <EvidenceSelector
            orgId={org.id}
            lang={lang}
            value={values.approvalEvidenceId}
            onChange={(v) => set('approvalEvidenceId', v)}
          />
        </div>
      </div>

      {/* ── SECTION C1: CHANGE HISTORY ────────────────────────────────── */}
      <div ref={changeHistoryRef} style={sectionSt}>
        <SectionHeader
          label={t.sectionC1}
          tip={t.sectionC1Tip}
          style={{ marginBottom: '1rem' }}
        />
        {formErrors.changeHistory && (
          <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0.5rem 0 0' }}>
            {t.changeHistoryRequired}
          </p>
        )}
      </div>

      {values.changeHistory.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: '1rem', borderRadius: 6, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: SURFACE_WHITE }}>
            <thead>
              <tr>
                <th style={thSt}>{t.chColVersion} <span style={{ color: ERROR_TEXT }}>{t.required}</span></th>
                <th style={thSt}>{t.chColDate} <span style={{ color: ERROR_TEXT }}>{t.required}</span></th>
                <th style={thSt}>{t.chColAuthor} <span style={{ color: ERROR_TEXT }}>{t.required}</span></th>
                <th style={{ ...thSt, width: '100%' }}>{t.chColSummary}</th>
              </tr>
            </thead>
            <tbody>
              {values.changeHistory.map((entry, idx) => (
                <tr key={idx}>
                  <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{entry.version}</td>
                  <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{entry.date}</td>
                  <td style={tdSt}>{resolvePerson(entry.author, people)}</td>
                  <td style={tdSt}>{entry.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add change entry */}
      <div style={{ display: 'grid', gridTemplateColumns: '6rem 8rem 1fr 2fr auto', gap: '0.5rem', alignItems: 'end', marginBottom: '1.5rem' }}>
        <div>
          <label style={labelSt}>{t.chColVersion} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <input value={values.chVersion} onChange={(e) => set('chVersion', e.target.value)} placeholder="1.1" style={chInputSt} />
        </div>
        <div>
          <label style={labelSt}>{t.chColDate} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <input type="date" value={values.chDate} onChange={(e) => set('chDate', e.target.value)} style={chInputSt} />
        </div>
        <div>
          <label style={labelSt}>{t.chColAuthor} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <PeopleSelector orgId={org.id} lang={lang} value={values.chAuthor} onChange={(val) => set('chAuthor', val)} disabled={saving} />
        </div>
        <div>
          <label style={labelSt}>{t.chColSummary}</label>
          <input value={values.chSummary} onChange={(e) => set('chSummary', e.target.value)} style={chInputSt} />
        </div>
        <button
          type="button"
          onClick={handleAddChangeEntry}
          disabled={!values.chVersion.trim() || !values.chDate || !values.chAuthor.trim()}
          style={{
            ...BTN_PRIMARY,
            background: (!values.chVersion.trim() || !values.chDate || !values.chAuthor.trim()) ? TEXT_DISABLED : BTN_PRIMARY.background,
            border: 'none',
            borderRadius: 6, padding: '0.35rem 0.75rem',
            cursor: (!values.chVersion.trim() || !values.chDate || !values.chAuthor.trim()) ? 'default' : 'pointer',
            ...FONT_BUTTON,
            whiteSpace: 'nowrap',
          }}
        >
          {t.chAddEntry}
        </button>
      </div>

      {/* ── SECTION C2: REVIEW HISTORY ────────────────────────────────── */}
      <div style={sectionSt}>
        <SectionHeader
          label={t.sectionC2}
          tip={t.sectionC2Tip}
          style={{ marginBottom: '1rem' }}
        />
      </div>

      {values.reviewHistory.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: '1rem', borderRadius: 6, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: SURFACE_WHITE }}>
            <thead>
              <tr>
                <th style={thSt}>{t.rvColDate}</th>
                <th style={thSt}>{t.rvColReviewedBy}</th>
                <th style={thSt}>{t.rvColOutcome}</th>
                <th style={thSt}>{t.rvColNotes}</th>
                <th style={{ ...thSt, width: '100%' }}>{t.rvColEvidence}</th>
              </tr>
            </thead>
            <tbody>
              {values.reviewHistory.map((entry, idx) => {
                const ev = entry.evidenceId ? evidences.find((e) => e.id === entry.evidenceId) : null;
                return (
                  <tr key={idx}>
                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{entry.date}</td>
                    <td style={tdSt}>{resolvePerson(entry.reviewedBy, people)}</td>
                    <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{outcomeLabel(entry.outcome)}</td>
                    <td style={tdSt}>{entry.notes}</td>
                    <td style={tdSt}>{ev ? evidenceDisplayLabel(ev) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add review entry */}
      <div style={{ display: 'grid', gridTemplateColumns: '8rem 1fr 9rem 1fr 1fr auto', gap: '0.5rem', alignItems: 'end', marginBottom: '0.6rem' }}>
        <div>
          <label style={labelSt}>{t.rvColDate} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <input type="date" value={values.rvDate} onChange={(e) => set('rvDate', e.target.value)} style={chInputSt} />
        </div>
        <div>
          <label style={labelSt}>{t.rvColReviewedBy} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <PeopleSelector orgId={org.id} lang={lang} value={values.rvReviewedBy} onChange={(val) => set('rvReviewedBy', val)} disabled={saving} />
        </div>
        <div>
          <label style={labelSt}>{t.rvColOutcome} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <select
            value={values.rvOutcome}
            onChange={(e) => set('rvOutcome', e.target.value)}
            style={{ ...chInputSt, cursor: 'pointer' }}
          >
            {REVIEW_OUTCOMES.map((o) => (
              <option key={o} value={o}>{outcomeLabel(o)}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelSt}>{t.rvColNotes}</label>
          <input value={values.rvNotes} onChange={(e) => set('rvNotes', e.target.value)} style={chInputSt} />
        </div>
        <div>
          <label style={labelSt}>{t.rvColEvidence}</label>
          <EvidenceSelector
            orgId={org.id}
            lang={lang}
            value={values.rvEvidenceId}
            onChange={(val) => set('rvEvidenceId', val)}
            multiple={false}
            disabled={saving}
            density="compact"
          />
        </div>
        <button
          type="button"
          onClick={handleAddReviewEntry}
          disabled={!values.rvDate || !values.rvReviewedBy.trim() || !values.rvOutcome}
          style={{
            ...BTN_PRIMARY,
            background: (!values.rvDate || !values.rvReviewedBy.trim() || !values.rvOutcome) ? TEXT_DISABLED : BTN_PRIMARY.background,
            border: 'none',
            borderRadius: 6, padding: '0.35rem 0.75rem',
            cursor: (!values.rvDate || !values.rvReviewedBy.trim() || !values.rvOutcome) ? 'default' : 'pointer',
            ...FONT_BUTTON,
            whiteSpace: 'nowrap',
          }}
        >
          {t.rvAddEntry}
        </button>
      </div>

      {/* Hint: outcome implies content change */}
      {values.rvOutcome !== 'No changes' && (
        <p style={{
          ...FONT_LABEL,
          color: UNSAVED_AMBER, margin: '0 0 0.75rem', lineHeight: 1.5,
        }}>
          {t.rvHintChanged}
        </p>
      )}

      {/* Confirmation: nextReviewDate auto-updated */}
      {reviewMsg && (
        <p style={{
          ...FONT_LABEL,
          color: '#15803d', margin: '0 0 0.75rem',
        }}>
          {t.rvNextReviewUpdated}
        </p>
      )}

      </fieldset>

      {/* Sticky form actions */}
      {!isView && (
        <div style={{
          position: 'sticky', bottom: 0,
          marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-1.5rem',
          marginTop: '1.5rem', padding: '0.85rem 1.5rem',
          background: BG_SURFACE, borderTop: `1px solid ${BORDER_DEFAULT}`,
          borderBottomLeftRadius: 10, borderBottomRightRadius: 10, zIndex: 10,
        }}>
          {saveError && (
            <div style={{
              background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
              borderRadius: 6, padding: '0.35rem 0.75rem',
              ...FONT_LABEL,
              marginBottom: '0.5rem',
            }}>{t.saveError}</div>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              {mode === 'edit' && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={saving}
                  style={{
                    ...BTN_DESTRUCTIVE_OUTLINE,
                    borderRadius: 8,
                    padding: '0.5rem 1rem',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    ...FONT_LABEL,
                  }}
                >{t.deleteFormBtn}</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {hasFormErrors && (
                <span style={{
                  ...FONT_LABEL,
                  color: ERROR_TEXT,
                }}>
                  {t.formValidationError}
                </span>
              )}
              {dirty && (
                <span style={{
                  ...FONT_LABEL,
                  color: UNSAVED_AMBER,
                }}>{t.unsavedLabel}</span>
              )}
              <button
                type="button"
                onClick={onCancel}
                style={{
                  ...BTN_SECONDARY,
                  borderRadius: 8, padding: '0.55rem 1.25rem', cursor: 'pointer',
                  ...FONT_BUTTON,
                }}
              >
                {t.formCancel}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  ...BTN_PRIMARY, background: saving ? TEXT_MUTED : BTN_PRIMARY.background, border: 'none',
                  borderRadius: 8, padding: '0.55rem 1.25rem', cursor: saving ? 'not-allowed' : 'pointer',
                  ...FONT_BUTTON,
                }}
              >
                {saving ? t.saving : t.formSave}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
export default function DocumentsView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult, typeFilter = null, initialEditId = null }) {
  const t = T[lang] || T.en;

  const [documents,      setDocuments]      = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [saveState,      setSaveState]      = useState('idle'); // 'idle' | 'error' — per-record error feedback
  const [editingDoc,     setEditingDoc]     = useState(null);
  const [formMode,       setFormMode]       = useState('create');
  const [filterType,     setFilterType]     = useState('All');
  const [filterStatus,   setFilterStatus]   = useState('All');
  const [filterReviewStatus, setFilterReviewStatus] = useState('All');
  const [searchText,     setSearchText]     = useState('');
  const [selectedIds,    setSelectedIds]    = useState(new Set());
  const [confirmState,   setConfirmState]   = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);
  const [dirty, setDirty]                  = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  const [formInstanceTick, setFormInstanceTick] = useState(0);
  const [showImport, setShowImport] = useState(false);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next form save reports back via onSaveResult instead of running
  // the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef                  = useRef(false);
  const lastProcessedAppTickRef            = useRef(0);

  useScrollToTopOnFormOpen(editingDoc !== null);

  // ── Mount: load documents ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    listOrgCollection(org.id, 'documents')
      .then((docs) => {
        if (!cancelled) {
          const sorted = [...docs].sort((a, b) =>
            (a.ref || a.id).localeCompare(b.ref || b.id)
          );
          setDocuments(sorted);
        }
      })
      .catch(() => { if (!cancelled) setDocuments([]); });
    return () => { cancelled = true; };
  }, [org.id]);

  // Deep-link from Dashboard Document gaps panel — open the target document
  // once the list has loaded. Fires once per incoming initialEditId.
  useEffect(() => {
    if (!initialEditId || !Array.isArray(documents)) return;
    const target = documents.find(d => d.id === initialEditId);
    if (target) {
      setEditingDoc({ docId: target.id, values: docToFormValues(target) });
      setFormMode(isRecordImmutable(target, 'document') ? 'view' : 'edit');
    }
  }, [initialEditId, documents]);

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (editingDoc) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const handleNewDoc = (isIsp = false) => {
    setFormMode('create');
    setEditingDoc({ docId: null, values: blankFormValues(org, typeFilter, isIsp, lang) });
    setDirty(false);
  };

  const handleSetAsIsp = () => {
    if (!editingDoc || !editingDoc.docId) return;
    const target = (documents || []).find(d => d.id === editingDoc.docId);
    if (!target || target.type !== 'Policy' || target.isInformationSecurityPolicy === true) return;
    const currentIsp = (documents || []).find(d => d.isInformationSecurityPolicy === true);
    const message = dirty
      ? `${t.setAsIspConfirm}\n\n${t.setAsIspDirtyWarning}`
      : t.setAsIspConfirm;
    setConfirmState({
      message,
      confirmLabel: t.setAsIspConfirmBtn,
      onConfirm: async () => {
        setConfirmState(null);
        const now = new Date().toISOString();
        const changedBy = org.consultantPersonId || 'system';
        const promotedPayload = { ...target, isInformationSecurityPolicy: true, updatedAt: now };
        const promoted = { ...promotedPayload, changeLog: appendChangeLog(target, promotedPayload, changedBy) };

        try {
          if (currentIsp && currentIsp.id !== target.id) {
            const demotedPayload = { ...currentIsp, isInformationSecurityPolicy: false, updatedAt: now };
            const demoted = { ...demotedPayload, changeLog: appendChangeLog(currentIsp, demotedPayload, changedBy) };
            await writeBidirectional(
              org.id,
              { path: `documents/${demoted.id}.json`, data: demoted, previousData: currentIsp },
              { path: `documents/${promoted.id}.json`, data: promoted },
            );
            setDocuments(prev => (prev || []).map(d => {
              if (d.id === demoted.id) return demoted;
              if (d.id === promoted.id) return promoted;
              return d;
            }));
          } else {
            await writeOrgFile(org.id, `documents/${promoted.id}.json`, promoted);
            setDocuments(prev => (prev || []).map(d => d.id === promoted.id ? promoted : d));
          }
          setEditingDoc({ docId: promoted.id, values: docToFormValues(promoted) });
          setDirty(false);
          setFormInstanceTick(n => n + 1);
        } catch {
          setSaveState('error');
          setTimeout(() => setSaveState('idle'), 3000);
        }
      },
    });
  };

  const handleEdit = (doc) => {
    // §11 rule 34 — Superseded/Retired documents are immutable; open
    // read-only even if the user clicked Edit. Reopen is only available via
    // Settings.
    setFormMode(isRecordImmutable(doc, 'document') ? 'view' : 'edit');
    setEditingDoc({ docId: doc.id, values: docToFormValues(doc) });
    setDirty(false);
  };

  const closeForm = () => {
    setDirty(false);
    setEditingDoc(null);
  };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  const handleView = (doc) => {
    setFormMode('view');
    setEditingDoc({ docId: doc.id, values: docToFormValues(doc) });
  };

  // ── §11 rule 42 helpers ─────────────────────────────────────────────────
  const handleDeactivateDocument = async (doc) => {
    try {
      const existing = await readOrgFile(org.id, `documents/${doc.id}.json`).catch(() => doc);
      const patch = { status: 'Retired' };
      const merged = { ...existing, ...patch, updatedAt: new Date().toISOString() };
      merged.changeLog = appendChangeLog(existing, patch, org.consultantPersonId || 'system');
      await writeOrgFile(org.id, `documents/${doc.id}.json`, merged);
      setDocuments(prev => (prev || []).map(d => d.id === doc.id ? merged : d));
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const handleDeleteFromForm = async () => {
    if (!editingDoc || !editingDoc.docId) return;
    const doc = (documents || []).find(d => d.id === editingDoc.docId);
    if (!doc) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'document', doc.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm,
        confirmLabel: t.deleteFormBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `documents/${doc.id}.json`);
            setDocuments((prev) => (prev || []).filter((d) => d.id !== doc.id));
            setSelectedIds((prev) => { const next = new Set(prev); next.delete(doc.id); return next; });
            closeForm();
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
          }
        },
      });
      return;
    }
    const showDeactivate = doc.status !== 'Retired';
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
      secondaryAction: showDeactivate ? {
        label: t.deactivateInstead,
        color: BTN_DESTRUCTIVE_OUTLINE,
        onClick: () => { setConfirmState(null); handleDeactivateDocument(doc); },
      } : undefined,
    });
  };

  const handleDelete = async (doc) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'document', doc.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm,
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `documents/${doc.id}.json`);
            setDocuments((prev) => (prev || []).filter((d) => d.id !== doc.id));
            setSelectedIds((prev) => { const next = new Set(prev); next.delete(doc.id); return next; });
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
          }
        },
      });
      return;
    }
    const showDeactivate = doc.status !== 'Retired';
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
      secondaryAction: showDeactivate ? {
        label: t.deactivateInstead,
        color: BTN_DESTRUCTIVE_OUTLINE,
        onClick: () => { setConfirmState(null); handleDeactivateDocument(doc); },
      } : undefined,
    });
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (checked) next.add(id); else next.delete(id); return next; });
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (scanningDelete) return;
    const items = (documents || []).filter(d => ids.includes(d.id));
    setScanningDelete(true);
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(d => scanReferencesToEntity(org.id, 'document', d.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((d, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(d);
      else blocked.push({ record: d, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohortIds) => {
      await Promise.all(cohortIds.map((id) => deleteOrgFile(org.id, `documents/${id}.json`).catch(() => {})));
      setDocuments((prev) => (prev || []).filter((d) => !cohortIds.includes(d.id)));
      setSelectedIds(new Set());
    };
    if (blocked.length === 0) {
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{n}', ids.length),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(ids);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.title || record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable.map(d => d.id));
      },
    });
  };

  const handleFormSave = async (values) => {
    // §11 rule 34 — immutability after closure. Short-circuit before any
    // FileSystemAdapter write so a Superseded/Retired document can only be
    // changed via the Settings reopen flow.
    const existingDoc = (documents || []).find(d => d.id === editingDoc?.docId);
    if (existingDoc && isRecordImmutable(existingDoc, 'document')) {
      setSaving(false);
      return;
    }
    setSaving(true);
    const cleanValues = stripEphemeral(values);
    // Convert empty documentId to null
    if (cleanValues.documentId === '') cleanValues.documentId = null;

    const allDocs   = documents || [];
    const now       = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';

    let savedDoc;
    if (!editingDoc.docId) {
      const year = new Date().getFullYear();
      const docPrefix = (org.refPrefixes && org.refPrefixes.document) || 'doc';
      const refPrefix = `${docPrefix}-${year}-`;
      let maxRefSeq = 0;
      allDocs.forEach(d => {
        if (d.ref && d.ref.startsWith(refPrefix)) {
          const n = parseInt(d.ref.slice(refPrefix.length), 10);
          if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
        }
      });
      const seq   = maxRefSeq + 1;
      const newId = makeDocId(seq);
      const ref   = makeRef(seq, docPrefix);
      savedDoc = {
        ...cleanValues,
        id: newId, orgId: org.id, ref,
        filePath: null, createdAt: now, updatedAt: now,
        changeLog: [],
      };
    } else {
      const prev = allDocs.find((d) => d.id === editingDoc.docId);
      savedDoc = {
        ...cleanValues,
        id: prev.id, orgId: prev.orgId, ref: prev.ref,
        filePath: prev.filePath, createdAt: prev.createdAt, updatedAt: now,
      };
      savedDoc.changeLog = appendChangeLog(prev, savedDoc, changedBy);
    }

    try {
      await writeOrgFile(org.id, `documents/${savedDoc.id}.json`, savedDoc);
      setDocuments((prev) => {
        const list = prev || [];
        const idx = list.findIndex((d) => d.id === savedDoc.id);
        if (idx >= 0) {
          const updated = [...list];
          updated[idx] = savedDoc;
          return updated;
        }
        return [...list, savedDoc];
      });
      setSaving(false);
      setEditingDoc({ docId: savedDoc.id, values: docToFormValues(savedDoc) });
      if (formMode === 'create') setFormMode('edit');
      setDirty(false);
      setSaveRequestTick(0);
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(true);
      }
    } catch {
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(false);
      }
      setSaving(false);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  if (documents === null) {
    return (
      <div style={{ padding: '2rem', ...FONT_LABEL, color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  const allTypeDocs = typeFilter ? documents.filter((d) => d.type === typeFilter) : documents;
  const isPolicyView = typeFilter === 'Policy';
  const ispDoc = isPolicyView ? allTypeDocs.find((d) => d.isInformationSecurityPolicy === true) : null;
  const displayDocs = isPolicyView
    ? allTypeDocs.filter((d) => d.isInformationSecurityPolicy !== true)
    : allTypeDocs;

  const cntTotal    = allTypeDocs.length;
  const cntApproved = allTypeDocs.filter((d) => d.status === 'Approved').length;
  const cntDraft    = allTypeDocs.filter((d) => d.status === 'Draft').length;
  const cntOther    = allTypeDocs.filter((d) => ['In Review', 'Superseded', 'Retired'].includes(d.status)).length;

  const visibleDocs = displayDocs.filter((d) => {
    if (!typeFilter && filterType !== 'All' && d.type !== filterType) return false;
    if (filterStatus !== 'All' && d.status !== filterStatus) return false;
    if (filterReviewStatus !== 'All' && dateStatus(d.nextReviewDate) !== filterReviewStatus) return false;
    if (searchText && !(d.title || '').toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  const selectedInView = visibleDocs.filter((d) => selectedIds.has(d.id));
  const allChecked     = visibleDocs.length > 0 && selectedInView.length === visibleDocs.length;
  const someChecked    = selectedInView.length > 0;
  const toggleAll = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) visibleDocs.forEach((d) => next.add(d.id));
      else         visibleDocs.forEach((d) => next.delete(d.id));
      return next;
    });
  };

  const thStyle = {
    ...FONT_TINY, color: TEXT_MUTED,
    letterSpacing: '0.05em', textTransform: 'uppercase', padding: '0.55rem 0.75rem',
    textAlign: 'left', borderBottom: `1px solid ${BORDER_DEFAULT}`, whiteSpace: 'nowrap',
    background: BG_SUBTLE,
  };

  const tdStyle = { padding: '0.55rem 0.75rem', borderBottom: `1px solid ${BG_SHELL}`, verticalAlign: 'middle' };

  const selectStyle = {
    ...FONT_LABEL, border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 6, padding: '0.35rem 0.55rem', background: BG_SURFACE,
    color: TEXT_DEFAULT, cursor: 'pointer', outline: 'none',
  };

  // ── Import view ──────────────────────────────────────────────────────────
  if (showImport && !editingDoc) {
    return (
      <div>
        <ImportWizard
          lang={lang}
          dedupeKey="title"
          onClose={() => setShowImport(false)}
          onImport={async (rows) => {
            const existing = await listOrgCollection(org.id, 'documents').catch(() => []);
            const now = new Date().toISOString();
            const year = new Date().getFullYear();
            const docPrefix = (org.refPrefixes && org.refPrefixes.document) || 'doc';
            const refPrefix = `${docPrefix}-${year}-`;
            let maxRefSeq = 0;
            existing.forEach(d => {
              if (d.ref && d.ref.startsWith(refPrefix)) {
                const n = parseInt(d.ref.slice(refPrefix.length), 10);
                if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
              }
            });

            const toWrite = [];
            for (const row of rows) {
              const dup = existing.find(d => d.title && d.title.toLowerCase() === (row.title || '').toLowerCase());
              if (dup) continue;

              const seq = ++maxRefSeq;
              const id  = makeDocId(seq);
              const ref = makeRef(seq, docPrefix);

              const docObj = {
                id, orgId: org.id, ref,
                documentId:   row.documentId || null,
                title:        row.title,
                type:         row.type || 'Policy',
                version:      row.version || '1.0',
                status:       row.status || 'Draft',
                owner:        row.owner || '',
                approvedBy:   [],
                approvalDate: null,
                nextReviewDate: row.nextReviewDate || '',
                classification: row.classification || ((org && org.defaultClassification) || 'INTERNAL'),
                clauseRefs:   [],
                linkedControlIds: [],
                acknowledgmentRequired: false,
                norms:        org.norms || [],
                language:     row.language || null,
                effectiveDate: row.effectiveDate || null,
                approvalEvidenceId: null,
                isInformationSecurityPolicy: false,
                changeHistory: [{
                  version: row.version || '1.0',
                  date:    row.changeDate   || todayStr(),
                  author:  row.changeAuthor || '',
                  summary: row.changeSummary || (lang === 'es' ? 'Creación' : 'Creation'),
                }],
                reviewHistory: [],
                filePath: null,
                changeLog: [],
                createdAt: now, updatedAt: now,
              };
              toWrite.push(docObj);
              existing.push(docObj);
            }

            try {
              await Promise.all(
                toWrite.map(d => writeOrgFile(org.id, `documents/${d.id}.json`, d))
              );
            } catch (err) {
              const fetched = await listOrgCollection(org.id, 'documents').catch(() => []);
              const sorted = [...fetched].sort((a, b) => (a.ref || a.id).localeCompare(b.ref || b.id));
              setDocuments(sorted);
              const msg = lang === 'es'
                ? 'No se pudieron guardar todos los documentos. Inténtalo de nuevo.'
                : 'Could not save all documents. Please try again.';
              throw new Error(msg);
            }

            const fetched = await listOrgCollection(org.id, 'documents').catch(() => []);
            const sorted = [...fetched].sort((a, b) => (a.ref || a.id).localeCompare(b.ref || b.id));
            setDocuments(sorted);
            return toWrite.length;
          }}
          columns={[
            { key: 'title',          label: 'Title',           required: true,  type: 'string' },
            { key: 'type',           label: 'Type',            required: true,  type: 'string',  enum: DOC_TYPES,         caseInsensitive: true },
            { key: 'version',        label: 'Version',         required: false, type: 'string',  default: '1.0' },
            { key: 'status',         label: 'Status',          required: false, type: 'string',  enum: DOCUMENT_STATUSES, caseInsensitive: true, default: 'Draft' },
            { key: 'owner',          label: 'Owner',           required: true,  type: 'string' },
            { key: 'classification', label: 'Classification',  required: false, type: 'string',  default: '' },
            { key: 'language',       label: 'Language',        required: false, type: 'string',  enum: ['es', 'en'],      default: null },
            { key: 'effectiveDate',  label: 'Effective Date',  required: false, type: 'string',  default: null },
            { key: 'nextReviewDate', label: 'Next Review Date',required: false, type: 'string',  default: '' },
            { key: 'documentId',     label: 'Document ID',     required: false, type: 'string',  default: null },
            { key: 'changeDate',    label: 'Change Date',    required: false, type: 'string',  default: '' },
            { key: 'changeAuthor',  label: 'Change Author',  required: false, type: 'string',  default: '' },
            { key: 'changeSummary', label: 'Change Summary', required: false, type: 'string',  default: '' },
          ]}
          templateFilename="import-documents.csv"
          title={{ es: t.importTitle, en: t.importTitle }}
        />
      </div>
    );
  }

  // ── Form view (separate screen, hides the list entirely) ─────────────────
  if (editingDoc) {
    return (
      <>
        <DocumentForm
          key={`${editingDoc.docId || 'new'}-${formInstanceTick}`}
          t={t}
          org={org}
          lang={lang}
          mode={formMode}
          docId={editingDoc.docId}
          initialValues={editingDoc.values}
          saving={saving}
          saveError={saveState === 'error'}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onDelete={handleDeleteFromForm}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
          typeFilter={typeFilter}
          onSetAsIsp={handleSetAsIsp}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
      </>
    );
  }

  return (
    <div>
      {/* ── Header bar ───────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0 }}>
            {typeFilter === 'Policy' ? t.titlePolicy : t.title}
          </h1>
        </div>
        {!isPolicyView && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', alignSelf: 'flex-start', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleNewDoc()}
              style={{ ...BTN_PRIMARY, border: 'none', borderRadius: 8, padding: '0.55rem 1.1rem', cursor: 'pointer', ...FONT_BUTTON, whiteSpace: 'nowrap' }}
            >
              {t.newDoc}
            </button>
            <button
              onClick={() => setShowImport(true)}
              style={{ ...BTN_SECONDARY, borderRadius: 8, padding: '0.55rem 1.1rem', cursor: 'pointer', ...FONT_BUTTON, whiteSpace: 'nowrap' }}
            >
              {t.importBtn}
            </button>
          </div>
        )}
      </div>

      {/* ── Summary bar (hidden in PolicyView) ───────────────────────────── */}
      {!isPolicyView && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <SummaryCard value={cntTotal}    label={t.cntTotal}    color={TEXT_HEADING} />
          <SummaryCard value={cntApproved} label={t.cntApproved} color={BRAND_PRIMARY_DARK} />
          <SummaryCard value={cntDraft}    label={t.cntDraft}    color={STATUS_WARN.text} />
          <SummaryCard value={cntOther}    label={t.cntOther}    color={TEXT_MUTED} />
        </div>
      )}

      {/* ── ISP zone (Policy view only) ──────────────────────────────────── */}
      {isPolicyView && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            ...FONT_SMALL,
            color: TEXT_MUTED, letterSpacing: '0.05em', textTransform: 'uppercase',
            fontWeight: 600, marginBottom: '0.75rem',
          }}>
            {t.ispZoneTitle}
          </div>
          {!ispDoc ? (
            <div style={{
              background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
              borderRadius: 10, padding: '1.75rem',
            }}>
              <p style={{
                ...FONT_SECTION_TITLE, fontWeight: 700, fontSize: '1rem',
                color: TEXT_HEADING, margin: '0 0 0.5rem',
              }}>
                {t.ispEmptyHeading}
              </p>
              <p style={{
                ...FONT_LABEL,
                color: TEXT_DEFAULT, lineHeight: 1.55, margin: '0 0 1rem',
              }}>
                {t.ispEmptyText}
              </p>
              <button
                onClick={() => handleNewDoc(true)}
                style={{
                  ...BTN_PRIMARY, border: 'none', borderRadius: 8,
                  padding: '0.55rem 1.1rem', cursor: 'pointer',
                  ...FONT_BUTTON,
                }}
              >
                {t.ispCreateBtn}
              </button>
            </div>
          ) : (
            (() => {
              const sc = DOC_STATUS_COLORS[ispDoc.status] || DOC_STATUS_COLORS['Draft'];
              return (
                <div style={{
                  background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
                  borderRadius: 10, padding: '1rem 1.25rem',
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  flexWrap: 'wrap', justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                      <span style={{
                        ...FONT_SMALL,
                        color: TEXT_MUTED, whiteSpace: 'nowrap',
                      }}>
                        {ispDoc.ref || ispDoc.id}
                      </span>
                      <span style={{
                        ...FONT_BODY_STRONG, fontWeight: 700,
                        color: TEXT_HEADING,
                      }}>
                        {ispDoc.title}
                      </span>
                      <span style={{
                        background: sc.bg, color: sc.text, borderRadius: 5,
                        padding: '0.2rem 0.5rem', ...FONT_SMALL,
                        fontWeight: 600, whiteSpace: 'nowrap',
                      }}>
                        {t.statusLabels[ispDoc.status] || ispDoc.status}
                      </span>
                    </div>
                    {ispDoc.approvalDate && (
                      <span style={{
                        ...FONT_SMALL,
                        color: TEXT_MUTED,
                      }}>
                        {t.approvedOn} {ispDoc.approvalDate}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button
                      onClick={() => handleView(ispDoc)}
                      style={{ background: BG_SUBTLE, color: TEXT_DEFAULT, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5, padding: '0.25rem 0.6rem', cursor: 'pointer', ...FONT_SMALL }}
                    >
                      {t.viewBtn}
                    </button>
                    <button
                      onClick={() => handleEdit(ispDoc)}
                      style={{ background: BG_SUBTLE, color: TEXT_DEFAULT, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5, padding: '0.25rem 0.6rem', cursor: 'pointer', ...FONT_SMALL }}
                    >
                      {t.editBtn}
                    </button>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* ── Filter bar (hidden in PolicyView) ────────────────────────────── */}
      {!isPolicyView && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t.filterTitle}
            </span>
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t.searchPlaceholder}
              style={{ ...FONT_LIST_ITEM, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.35rem 0.65rem', background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none', minWidth: '16rem' }}
            />
          </div>
          {!typeFilter && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t.filterType}
              </span>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
                <option value="All">{t.all}</option>
                {DOC_TYPES.map((tp) => <option key={tp} value={tp}>{t.typeLabels[tp]}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t.filterStatus}
            </span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
              <option value="All">{t.all}</option>
              {DOC_STATUSES.map((s) => <option key={s} value={s}>{t.statusLabels[s]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t.filterReviewStatus}
            </span>
            <select value={filterReviewStatus} onChange={(e) => setFilterReviewStatus(e.target.value)} style={selectStyle}>
              <option value="All">{t.all}</option>
              <option value="overdue">{t.badgeOverdue}</option>
              <option value="soon">{t.badgeSoon}</option>
            </select>
          </div>
        </div>
      )}

      {/* ── Empty state (hidden in PolicyView) ───────────────────────────── */}
      {!isPolicyView && displayDocs.length === 0 && (
        <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '3rem', textAlign: 'center' }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>{t.emptyTitle}</p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>{t.emptyHint}</p>
        </div>
      )}

      {/* ── Bulk action bar (hidden in PolicyView) ───────────────────────── */}
      {!isPolicyView && someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', selectedInView.length)}
          </span>
          <button
            onClick={handleDeleteSelected}
            style={{ ...BTN_DESTRUCTIVE_OUTLINE, borderRadius: 5, padding: '0.2rem 0.5rem', cursor: 'pointer', ...FONT_SMALL }}
          >
            {t.deleteSelected}
          </button>
        </div>
      )}

      {/* ── Documents table (hidden in PolicyView) ───────────────────────── */}
      {!isPolicyView && displayDocs.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={t.selectAll}
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={{ ...thStyle, width: '6rem'  }}>{t.colRef}</th>
                <th style={{ ...thStyle, minWidth: 200  }}>{t.colTitle}</th>
                <th style={{ ...thStyle, width: '8rem'  }}>{t.colType}</th>
                <th style={{ ...thStyle, width: '5.5rem'}}>{t.colVersion}</th>
                <th style={{ ...thStyle, width: '8rem'  }}>{t.colStatus}</th>
                <th style={{ ...thStyle, width: '8.5rem'}}>{t.colNextReview}</th>
                <th style={{ ...thStyle, width: '10rem' }}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocs.map((doc) => {
                const sc      = DOC_STATUS_COLORS[doc.status] || DOC_STATUS_COLORS['Draft'];
                const cc      = classColor(doc.classification);
                const docBadge = (doc.status === 'Retired' || doc.status === 'Superseded')
                  ? null
                  : dateStatus(doc.nextReviewDate);

                return (
                  <tr key={doc.id}>
                    <td style={{ ...tdStyle, textAlign: 'center', padding: '0.5rem 0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(doc.id)}
                        onChange={(e) => toggleOne(doc.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {doc.ref || doc.id}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ ...FONT_BODY_STRONG, color: TEXT_HEADING }}>
                        {doc.title}
                      </span>
                      {doc.classification && (
                        <span style={{ marginLeft: '0.5rem', background: cc.bg, color: cc.text, borderRadius: 20, padding: '0.1rem 0.45rem', ...FONT_TINY, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                          {doc.classification}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ background: BG_SUBTLE, color: TEXT_DEFAULT, borderRadius: 5, padding: '0.2rem 0.5rem', ...FONT_SMALL, whiteSpace: 'nowrap' }}>
                        {t.typeLabels[doc.type] || doc.type}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {doc.version || '—'}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ background: sc.bg, color: sc.text, borderRadius: 5, padding: '0.2rem 0.5rem', ...FONT_SMALL, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {t.statusLabels[doc.status] || doc.status}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {doc.nextReviewDate || '—'}
                      {docBadge && (
                        <span style={{
                          ...FONT_TINY,
                          fontWeight: 600,
                          background: docBadge === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                          color:      docBadge === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                          borderRadius: 4,
                          padding: '0.15rem 0.45rem',
                          marginLeft: '0.35rem',
                          whiteSpace: 'nowrap',
                        }}>
                          {docBadge === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                          onClick={() => handleView(doc)}
                          style={{ background: BG_SUBTLE, color: TEXT_DEFAULT, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5, padding: '0.2rem 0.55rem', cursor: 'pointer', ...FONT_SMALL }}
                        >
                          {t.viewBtn}
                        </button>
                        <button
                          onClick={() => handleEdit(doc)}
                          style={{ background: BG_SUBTLE, color: TEXT_DEFAULT, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 5, padding: '0.2rem 0.55rem', cursor: 'pointer', ...FONT_SMALL }}
                        >
                          {t.editBtn}
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          style={{ ...BTN_DESTRUCTIVE_OUTLINE, borderRadius: 5, padding: '0.2rem 0.55rem', cursor: 'pointer', ...FONT_SMALL }}
                        >
                          {t.deleteBtn}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
